import { InjectionToken, Injectable, computed, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BannerDismissalService } from '../banner/banner-dismissal.service';
import { LoggerService } from '../logger/logger.service';
import {
  BeforeInstallPromptEvent,
  clearCapturedInstallPrompt,
  connectPwaInstallCapture,
} from './pwa-install-capture';
import {
  PwaInstallPlatform,
  PwaPlatformEnvironment,
  detectPwaInstallPlatform,
  isRunningStandalone,
} from './pwa-install-platform';

/** What the platform detection reads; a token so specs can supply UA fixtures. */
export const PWA_PLATFORM_ENVIRONMENT: InjectionToken<PwaPlatformEnvironment> =
  new InjectionToken<PwaPlatformEnvironment>('PWA_PLATFORM_ENVIRONMENT', {
    providedIn: 'root',
    factory: (): PwaPlatformEnvironment => ({
      userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent ?? '',
      maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0,
      isStandalone: typeof window !== 'undefined' && isRunningStandalone(window),
      isNativeShell: Capacitor.isNativePlatform(),
    }),
  });

/** localStorage/sessionStorage keys owned by this service. */
export const PWA_INSTALL_STORAGE_KEYS: {
  readonly VISIT_COUNT: 'pwaInstallVisitCount';
  readonly VISIT_COUNTED_THIS_SESSION: 'pwaInstallVisitCounted';
  readonly INSTALLED: 'pwaInstalled';
} = {
  VISIT_COUNT: 'pwaInstallVisitCount',
  VISIT_COUNTED_THIS_SESSION: 'pwaInstallVisitCounted',
  INSTALLED: 'pwaInstalled',
} as const;

/** "Not now" hides the automatic prompt for this long. Settings can still open it. */
export const PWA_INSTALL_DISMISS_DURATION_MS: number = 14 * 24 * 60 * 60 * 1000;
/** The automatic prompt waits for the user's second visit (browser session) to the app shell. */
export const PWA_INSTALL_MIN_VISITS: number = 2;
/** Dismissals are per device, not per account. */
export const PWA_INSTALL_DISMISSAL_SCOPE: string = 'device';

export type PwaInstallSource = 'auto' | 'settings';

export type PwaInstallOutcome =
  | 'prompt-shown'
  | 'native-accepted'
  | 'native-dismissed'
  | 'not-now'
  | 'closed'
  | 'installed';

const MOBILE_WEB_PLATFORMS: readonly PwaInstallPlatform[] = [
  'ios-safari',
  'ios-other',
  'android-chromium',
  'android-other',
  'in-app-browser',
];

/**
 * Owns "add Sunny Street to your home screen": which browser story applies, the captured
 * `beforeinstallprompt` event, whether the instructions sheet is open, and the visit/dismissal
 * bookkeeping behind the automatic prompt. The sheet itself is `PwaInstallPromptComponent`.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly logger = inject(LoggerService);
  private readonly bannerDismissal = inject(BannerDismissalService);

  private readonly _platform = signal<PwaInstallPlatform>(
    detectPwaInstallPlatform(inject(PWA_PLATFORM_ENVIRONMENT)),
  );
  private readonly _deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  private readonly _isInstalled = signal<boolean>(this.readFlag(PWA_INSTALL_STORAGE_KEYS.INSTALLED));
  private readonly _openedFrom = signal<PwaInstallSource | null>(null);
  private readonly visitCount: number = this.recordVisit();

  readonly platform = this._platform.asReadonly();
  readonly isInstalled = this._isInstalled.asReadonly();
  /** Android Chromium handed us a `beforeinstallprompt` we can show. */
  readonly canPromptNatively = computed<boolean>(() => this._deferredPrompt() !== null);
  /** Mobile browser that has not installed the app: the only place install help makes sense. */
  readonly isEligible = computed<boolean>(
    () => MOBILE_WEB_PLATFORMS.includes(this._platform()) && !this._isInstalled(),
  );
  readonly isOpen = computed<boolean>(() => this._openedFrom() !== null && this.isEligible());
  readonly openedFrom = this._openedFrom.asReadonly();

  constructor() {
    connectPwaInstallCapture({
      onPromptAvailable: (event: BeforeInstallPromptEvent) => this._deferredPrompt.set(event),
      onInstalled: () => this.markInstalled(),
    });
  }

  /** Whether the automatic (not user-requested) prompt may appear right now. */
  shouldAutoPrompt(): boolean {
    return (
      this.isEligible() &&
      this.visitCount >= PWA_INSTALL_MIN_VISITS &&
      !this.bannerDismissal.isDismissed('pwaInstall', PWA_INSTALL_DISMISSAL_SCOPE)
    );
  }

  open(source: PwaInstallSource): void {
    if (!this.isEligible() || this._openedFrom() !== null) return;
    this._openedFrom.set(source);
    this.onInstallOutcome('prompt-shown', source);
  }

  /** "Not now" on the automatic prompt snoozes it; closing a Settings-opened sheet does not. */
  dismiss(): void {
    const source: PwaInstallSource | null = this._openedFrom();
    if (!source) return;
    if (source === 'auto') {
      this.bannerDismissal.dismiss('pwaInstall', PWA_INSTALL_DISMISSAL_SCOPE, PWA_INSTALL_DISMISS_DURATION_MS);
    }
    this._openedFrom.set(null);
    this.onInstallOutcome(source === 'auto' ? 'not-now' : 'closed', source);
  }

  /** Closes the sheet without a snooze (e.g. the user navigated away from the automatic prompt). */
  close(): void {
    const source: PwaInstallSource | null = this._openedFrom();
    if (!source) return;
    this._openedFrom.set(null);
    this.onInstallOutcome('closed', source);
  }

  /** Shows Chromium's native install dialog. A deferred prompt is single-use. */
  async promptInstall(): Promise<void> {
    const event: BeforeInstallPromptEvent | null = this._deferredPrompt();
    if (!event) return;
    const source: PwaInstallSource = this._openedFrom() ?? 'auto';
    this._deferredPrompt.set(null);
    clearCapturedInstallPrompt();
    try {
      await event.prompt();
      const choice: { outcome: 'accepted' | 'dismissed' } = await event.userChoice;
      this.onInstallOutcome(choice.outcome === 'accepted' ? 'native-accepted' : 'native-dismissed', source);
      if (choice.outcome === 'accepted') {
        this._openedFrom.set(null);
      }
    } catch (error) {
      this.logger.warn('PWA install prompt failed', { error: String(error) });
    }
  }

  /**
   * Single funnel for install outcomes. Logs a breadcrumb today; product analytics can hook in
   * here without touching the UI.
   */
  onInstallOutcome(outcome: PwaInstallOutcome, source: PwaInstallSource | null = this._openedFrom()): void {
    this.logger.info('PWA install outcome', { outcome, source, platform: this._platform() });
  }

  private markInstalled(): void {
    this._isInstalled.set(true);
    this._openedFrom.set(null);
    this._deferredPrompt.set(null);
    this.writeFlag(PWA_INSTALL_STORAGE_KEYS.INSTALLED);
    this.onInstallOutcome('installed', null);
  }

  /** Counts browser sessions (not reloads) that reached the app; returns the running total. */
  private recordVisit(): number {
    try {
      const count: number = Number(localStorage.getItem(PWA_INSTALL_STORAGE_KEYS.VISIT_COUNT)) || 0;
      if (sessionStorage.getItem(PWA_INSTALL_STORAGE_KEYS.VISIT_COUNTED_THIS_SESSION)) return count;
      sessionStorage.setItem(PWA_INSTALL_STORAGE_KEYS.VISIT_COUNTED_THIS_SESSION, '1');
      localStorage.setItem(PWA_INSTALL_STORAGE_KEYS.VISIT_COUNT, String(count + 1));
      return count + 1;
    } catch {
      return 0;
    }
  }

  private readFlag(key: string): boolean {
    try {
      return localStorage.getItem(key) === 'yes';
    } catch {
      return false;
    }
  }

  private writeFlag(key: string): void {
    try {
      localStorage.setItem(key, 'yes');
    } catch {
      // Storage unavailable — persistence is best-effort
    }
  }
}
